import React, { useEffect, useState, useRef, useCallback } from "react";
import { useAuth } from "../../../firebase/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useAnalytics } from "../../../firebase/hooks/useAnalytics";
import Sidebar from "../../../components/Sidebar";
import LanguageSwitcher from "../../../components/LanguageSwitcher";
import {
    PdfPanelProvider,
    PdfPanelContext,
    PdfSidePanel,
} from "../../../components/MessageWithTableRenderer";

import { useChatSession } from "./useChatSession";
import { useFeedback } from "./useFeedback";
import { DEFAULT_AGENT } from "./constants";
import {
    BouncingDots,
    ChatInput,
    MessageBubble,
    FeedbackModal,
    FeedbackSuccessBanner,
    SampleQuestions,
    AgentSelector,
    GeminiAgentIntro,
    NTUSTAgentIntro,
} from "./components";

function AiChat() {
    const { currentUser, getUserProfile, isAuthReady } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { trackPageView, trackSearch } = useAnalytics();

    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);
    const dropdownRef = useRef(null);

    const profile = getUserProfile();

    // --- Language ---
    const getLangFromUrl = () => {
        const params = new URLSearchParams(location.search);
        return params.get("lang") === "zh" ? "zh" : "en";
    };

    const [language, setLanguage] = useState(getLangFromUrl());
    const chatLanguage = language === "zh" ? "zh-tw" : "en";

    // --- Agent ---
    const [selectedAgent, setSelectedAgent] = useState(DEFAULT_AGENT);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    // --- Input ---
    const [input, setInput] = useState("");

    // --- Chat session hook ---
    const {
        messages,
        sessionId,
        isLoading,
        handleAsk,
        handleReask,
        handleNewChat,
    } = useChatSession({
        language,
        chatLanguage,
        selectedAgent,
        setSelectedAgent,
    });

    // --- Feedback hook ---
    const {
        thumbsUpStates,
        thumbsDownStates,
        isModalOpen,
        setFeedbackReason,
        isFeedbackSubmitting,
        feedbackSuccess,
        toggleThumbsUp,
        openFeedbackModal,
        closeFeedbackModal,
        submitFeedback,
    } = useFeedback();

    // --- Effects ---

    // Track page view on mount
    useEffect(() => {
        trackPageView("Dashboard");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keep language in sync with URL
    useEffect(() => {
        setLanguage(getLangFromUrl());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // Redirect if not logged in
    useEffect(() => {
        if (!currentUser) {
            navigate({ pathname: "/login", search: `?lang=${language}` });
        }
    }, [currentUser, navigate, language]);

    // Update URL when language changes
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get("lang") !== language) {
            params.set("lang", language);
            navigate({ search: params.toString() }, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language]);

    // Auto-scroll to bottom when messages update
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    // Auto-focus textarea when loading completes
    useEffect(() => {
        if (!isLoading && textareaRef.current) {
            setTimeout(() => textareaRef.current?.focus(), 100);
        }
    }, [isLoading]);

    // Close agent dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // --- Handlers ---

    const handleSubmit = useCallback(() => {
        if (!input.trim()) return;
        trackSearch(input.trim());
        handleAsk(input);
        setInput("");
    }, [input, trackSearch, handleAsk]);

    const handleCopyToClipboard = useCallback(async (text) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch {
            const textArea = document.createElement("textarea");
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand("copy");
            document.body.removeChild(textArea);
        }
    }, []);

    const getAgentDisclaimer = () => {
        const disclaimers = {
            chatproxy_NTUST: {
                en: "NTUST Chatbot is powered by NTUST's own language model, designed to provide video response.",
                zh: "NTUST Chatbot 是由 NTUST 自行研發的語言模型所驅動，設計用於提供影片回應。",
            },
            chatproxy_v4: {
                en: "T-global agent is powered by Oomii. to provide most accurate response regarding T-global PIM products.",
                zh: "T-global agent 由 Oomii 支援，提供最精確的關於 T-global PIM 產品的回應。",
            },
        };
        const d = disclaimers[selectedAgent];
        if (!d) return "";
        return language === "en" ? d.en : d.zh;
    };

    // ===========================
    //         RENDER
    // ===========================

    // --- Loading / auth gate ---
    if (!currentUser || !profile || !isAuthReady) {
        return (
            <div className="flex h-screen bg-slate-100 dark:bg-dashboard-bg text-black dark:text-white font-sans">
                <LanguageSwitcher
                    language={language}
                    setLanguage={setLanguage}
                />
                <div className="flex-1 flex flex-col gap-8 items-center justify-center -mt-36">
                    <div className="flex flex-col gap-4 items-center text-center">
                        <div className="flex items-center gap-2">
                            <BouncingDots />
                            <span className="text-orange-500 text-lg font-semibold">
                                {language === "zh"
                                    ? "正在加載數據..."
                                    : "Loading data..."}
                            </span>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            {language === "zh"
                                ? "請稍候，正在為您準備個人資料面板。"
                                : "Please wait while we prepare your profile panel."}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // --- Empty state (no messages yet) ---
    if (messages.length === 0) {
        return (
            <div className="flex min-h-screen bg-slate-100 dark:bg-dashboard-bg text-black font-sans">
                <Sidebar language={language} />
                <LanguageSwitcher
                    language={language}
                    setLanguage={setLanguage}
                />

                <div className="flex-1 flex flex-col items-center justify-center overflow-y-auto py-8">
                    <h2 className="text-black dark:text-white text-4xl font-bold mb-4 text-center">
                        T-Global PIMS
                    </h2>
                    <p className="text-gray-800 dark:text-slate-300 text-lg mb-8 text-center max-w-md">
                        {language === "en"
                            ? "Try asking something here, start by saying 'Hi'"
                            : "試著在這裡問些什麼，先說聲 【Hi】"}
                    </p>

                    {/* Agent-specific intro content */}
                    <div className="md:w-4/5 w-11/12 flex flex-col gap-4 items-center justify-center m-2 mb-8 ">
                        {selectedAgent === "Gemini_Agent" && (
                            <GeminiAgentIntro language={language} />
                        )}

                        <div className="w-full grid grid-cols-1 gap-6">
                            {selectedAgent === "chatproxy_v4" && (
                                <div className="w-full flex items-center justify-center mx-auto">
                                    <SampleQuestions setInput={setInput} />
                                </div>
                            )}

                            {selectedAgent === "chatproxy_NTUST" && (
                                <div className="w-full flex items-center justify-center mx-auto">
                                    <NTUSTAgentIntro setInput={setInput} />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* History link */}
                    <div className="text-slate-800 dark:text-slate-300 text-md mb-8 text-center max-w-md">
                        {language === "en"
                            ? "You can continue from History also"
                            : "您也可以從歷史記錄中繼續"}{" "}
                        <button
                            className="text-orange-500 hover:underline"
                            onClick={() =>
                                navigate({
                                    pathname: "/History",
                                    search: `?lang=${language}`,
                                })
                            }
                        >
                            {language === "en" ? "click here" : "點擊這裡"}
                        </button>
                    </div>

                    {/* Input area */}
                    <div className="md:w-3/5 w-11/12 h-2/12 m-5 flex flex-col gap-2 items-center justify-center p-4 rounded-2xl bg-black/10 dark:bg-white/10 border border-black/20 dark:border-white/20 shadow-lg">
                        <ChatInput
                            input={input}
                            setInput={setInput}
                            isLoading={isLoading}
                            language={language}
                            onSubmit={handleSubmit}
                            disabled={selectedAgent === "chatproxy_NTUST"}
                            variant="full"
                        />

                        <AgentSelector
                            selectedAgent={selectedAgent}
                            setSelectedAgent={setSelectedAgent}
                            isDropdownOpen={isDropdownOpen}
                            setIsDropdownOpen={setIsDropdownOpen}
                            dropdownRef={dropdownRef}
                        />
                    </div>
                </div>
            </div>
        );
    }

    // --- Conversation state (messages present) ---
    return (
        <PdfPanelProvider>
            <PdfPanelContext.Consumer>
                {({ openPdfs }) => (
                    <div className="flex w-screen h-screen bg-slate-100 dark:bg-dashboard-bg text-black font-sans">
                        <Sidebar language={language} />
                        {openPdfs.length === 0 && (
                            <LanguageSwitcher
                                language={language}
                                setLanguage={setLanguage}
                            />
                        )}

                        {/* Main chat area — shrinks when PDF panel is open */}
                        <div
                            className={`flex-1 h-full flex flex-col pt-16 md:pt-12 md:pl-0 transition-all duration-300 min-w-0`}
                        >
                            <div className="w-full h-full flex flex-col items-center justify-center mx-auto px-4 md:px-0 w-[90%]">
                                {feedbackSuccess && (
                                    <FeedbackSuccessBanner
                                        language={language}
                                    />
                                )}

                                {/* Messages list */}
                                <div className="w-full md:w-5/6 h-[70%] md:h-[65%] flex flex-col rounded-xl overflow-y-auto no-scrollbar gap-6 md:gap-12 px-2">
                                    {messages.map((msg) => (
                                        <div key={msg.id} className="w-full">
                                            <MessageBubble
                                                msg={msg}
                                                messages={messages}
                                                language={language}
                                                isLoading={isLoading}
                                                sessionId={sessionId}
                                                thumbsUpStates={thumbsUpStates}
                                                thumbsDownStates={
                                                    thumbsDownStates
                                                }
                                                onCopy={handleCopyToClipboard}
                                                onThumbsUp={toggleThumbsUp}
                                                onThumbsDown={openFeedbackModal}
                                                onRetry={handleReask}
                                            />
                                        </div>
                                    ))}
                                    <div ref={messagesEndRef} />
                                </div>

                                {/* Input area */}
                                <div className="w-full md:w-[70%] h-auto md:h-2/12 mx-2 md:mx-5 my-3 md:my-5 flex flex-col items-center justify-center p-2 md:p-4 rounded-2xl bg-black/10 dark:bg-white/10 border border-black/20 dark:border-white/20 shadow-lg">
                                    <ChatInput
                                        input={input}
                                        setInput={setInput}
                                        isLoading={isLoading}
                                        language={language}
                                        onSubmit={handleSubmit}
                                        onNewChat={handleNewChat}
                                        textareaRef={textareaRef}
                                        variant="compact"
                                    />

                                    <div className="mt-2 text-xs text-black/70 dark:text-white/70 italic">
                                        {getAgentDisclaimer()}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* PDF side panel */}
                        {openPdfs.length > 0 && (
                            <div className="w-[420px] xl:w-[600px] flex-shrink-0 h-full transition-all duration-300">
                                <PdfSidePanel />
                            </div>
                        )}

                        {/* Feedback modal — rendered once globally, not per message */}
                        {isModalOpen && (
                            <FeedbackModal
                                language={language}
                                isFeedbackSubmitting={isFeedbackSubmitting}
                                setFeedbackReason={setFeedbackReason}
                                onClose={closeFeedbackModal}
                                onSubmit={submitFeedback}
                            />
                        )}
                    </div>
                )}
            </PdfPanelContext.Consumer>
        </PdfPanelProvider>
    );
}

export default AiChat;
